
import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../../services/api';
import { toast } from 'react-toastify';
import EditLeadForm from './EditLeadForm';
import useCrmStore from '../../../services/useCrmStore';
import '../CrmModals.css';

import {
  FiUser,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiDollarSign,
  FiCalendar,
  FiTarget,
  FiActivity,
  FiFileText,
  FiClock,
  FiMessageSquare,
  FiSend,
  FiAlertCircle,
  FiCheckCircle,
  FiTrendingUp,
  FiEdit,
  FiPlus,
  FiTrash2,
  FiX,
  FiShoppingCart,
  FiTrendingDown,
  FiCreditCard,
  FiPercent
} from 'react-icons/fi';

const LeadDetailModal = ({ lead, stages, onClose, onLeadUpdated }) => {
  const [currentLead, setCurrentLead] = useState(lead);
  const [showEditForm, setShowEditForm] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingNote, setSendingNote] = useState(false);
  const [activeTab, setActiveTab] = useState('info'); // 'info', 'sales', 'timeline'

  // Sales state
  const {
    sales,
    transactions,
    financialSummary,
    salesLoading,
    fetchLeadSales,
    fetchLeadTransactions,
    fetchLeadFinancialSummary,
    createSale,
    updateSale,
    deleteSale,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    clearSalesData
  } = useCrmStore();

  const [showSaleForm, setShowSaleForm] = useState(false);
  const [showTransactionForm, setShowTransactionForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);

  // Form state for sale
  const [saleForm, setSaleForm] = useState({
    sale_date: new Date().toISOString().split('T')[0],
    sale_value: '',
    discount: '0',
    payment_method: '',
    installments: 1,
    description: ''
  });

  // Form state for transaction
  const [transactionForm, setTransactionForm] = useState({
    type: 'REVENUE',
    amount: '',
    transaction_date: new Date().toISOString().split('T')[0],
    category: '',
    description: ''
  });

  const fetchLeadData = useCallback(async () => {
    setLoading(true);
    try {
      const [activitiesResponse, leadResponse] = await Promise.all([
        api.get(`/api/crm/leads/${lead.id}/activities`),
        api.get(`/api/crm/leads/${lead.id}`)
      ]);
      setTimeline(activitiesResponse.data);
      setCurrentLead(leadResponse.data);
    } catch (error) {
      console.error('Failed to fetch lead data:', error);
      toast.error('Falha ao carregar dados do lead');
    } finally {
      setLoading(false);
    }
  }, [lead.id]);

  const fetchSalesData = useCallback(async () => {
    try {
      await Promise.all([
        fetchLeadSales(lead.id),
        fetchLeadTransactions(lead.id),
        fetchLeadFinancialSummary(lead.id)
      ]);
    } catch (error) {
      console.error('Failed to fetch sales data:', error);
    }
  }, [lead.id, fetchLeadSales, fetchLeadTransactions, fetchLeadFinancialSummary]);

  useEffect(() => {
    fetchLeadData();
    fetchSalesData();

    return () => {
      clearSalesData();
    };
  }, [fetchLeadData, fetchSalesData, clearSalesData]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    setSendingNote(true);
    try {
      await api.post(`/api/crm/leads/${lead.id}/notes`, {
        content: newNote
      });
      setNewNote('');
      toast.success('Nota adicionada com sucesso');
      await fetchLeadData();
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Falha ao adicionar nota');
    } finally {
      setSendingNote(false);
    }
  };

  // Sale handlers
  const handleSaleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSale) {
        await updateSale(editingSale.id, saleForm);
        toast.success('Venda atualizada com sucesso');
      } else {
        await createSale(lead.id, saleForm);
        toast.success('Venda registrada com sucesso');
      }
      setShowSaleForm(false);
      setEditingSale(null);
      resetSaleForm();
      await fetchSalesData();
      await fetchLeadData();
      if (onLeadUpdated) onLeadUpdated();
    } catch (error) {
      toast.error('Falha ao salvar venda');
    }
  };

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta venda?')) return;
    try {
      await deleteSale(saleId);
      toast.success('Venda excluída com sucesso');
      await fetchSalesData();
      await fetchLeadData();
      if (onLeadUpdated) onLeadUpdated();
    } catch (error) {
      toast.error('Falha ao excluir venda');
    }
  };

  const handleEditSale = (sale) => {
    setEditingSale(sale);
    setSaleForm({
      sale_date: sale.sale_date,
      sale_value: sale.sale_value.toString(),
      discount: sale.discount.toString(),
      payment_method: sale.payment_method || '',
      installments: sale.installments || 1,
      description: sale.description || ''
    });
    setShowSaleForm(true);
  };

  const resetSaleForm = () => {
    setSaleForm({
      sale_date: new Date().toISOString().split('T')[0],
      sale_value: '',
      discount: '0',
      payment_method: '',
      installments: 1,
      description: ''
    });
  };

  // Transaction handlers
  const handleTransactionSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTransaction) {
        await updateTransaction(editingTransaction.id, transactionForm);
        toast.success('Transação atualizada com sucesso');
      } else {
        await createTransaction(lead.id, transactionForm);
        toast.success('Transação registrada com sucesso');
      }
      setShowTransactionForm(false);
      setEditingTransaction(null);
      resetTransactionForm();
      await fetchSalesData();
    } catch (error) {
      toast.error('Falha ao salvar transação');
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta transação?')) return;
    try {
      await deleteTransaction(transactionId);
      toast.success('Transação excluída com sucesso');
      await fetchSalesData();
    } catch (error) {
      toast.error('Falha ao excluir transação');
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setTransactionForm({
      type: transaction.type,
      amount: transaction.amount.toString(),
      transaction_date: transaction.transaction_date,
      category: transaction.category || '',
      description: transaction.description || ''
    });
    setShowTransactionForm(true);
  };

  const resetTransactionForm = () => {
    setTransactionForm({
      type: 'REVENUE',
      amount: '',
      transaction_date: new Date().toISOString().split('T')[0],
      category: '',
      description: ''
    });
  };

  const getActivityIcon = (type) => {
    const icons = {
      'LEAD_CREATED': <FiCheckCircle color="#22c55e" />,
      'STAGE_CHANGED': <FiTrendingUp color="#38bdf8" />,
      'NOTE_ADDED': <FiMessageSquare color="#8b5cf6" />,
      'EMAIL_SENT': <FiMail color="#f59e0b" />,
      'WHATSAPP_SENT': <FiMessageSquare color="#25d366" />,
      'LEAD_UPDATED': <FiEdit color="#0ea5e9" />,
      'AUTOMATION_TRIGGERED': <FiActivity color="#ec4899" />,
      'SALE_CREATED': <FiShoppingCart color="#22c55e" />,
      'REVENUE_ADDED': <FiTrendingUp color="#22c55e" />,
      'EXPENSE_ADDED': <FiTrendingDown color="#ef4444" />,
    };
    return icons[type] || <FiActivity color="#94a3b8" />;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getPriorityClass = (priority) => {
    const classes = {
      LOW: 'crm-priority-low',
      MEDIUM: 'crm-priority-medium',
      HIGH: 'crm-priority-high',
      URGENT: 'crm-priority-urgent'
    };
    return classes[priority] || classes.MEDIUM;
  };

  const getStatusClass = (status) => {
    const classes = {
      ACTIVE: 'crm-status-active',
      INACTIVE: 'crm-status-inactive',
      BLOCKED: 'crm-status-blocked',
      CONVERTED: 'crm-status-converted',
      LOST: 'crm-status-lost'
    };
    return classes[status] || classes.ACTIVE;
  };

  const getScoreColor = (score) => {
    if (score >= 75) return '#22c55e';
    if (score >= 50) return '#f59e0b';
    return '#ef4444';
  };

  if (showEditForm) {
    return (
      <EditLeadForm
        lead={currentLead}
        stages={stages}
        onClose={() => setShowEditForm(false)}
        onSuccess={async () => {
          await fetchLeadData();
          setShowEditForm(false);
          if (onLeadUpdated) onLeadUpdated();
        }}
      />
    );
  }

  return (
    <div className="crm-modal-overlay" onClick={onClose}>
      <div className="crm-modal crm-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-header">
          <div className="crm-modal-header-content">
            <h2 className="crm-modal-title">{currentLead.name}</h2>
            <div className="crm-flex-row crm-mt-2 crm-flex-wrap">
              <span className={`crm-priority-badge ${getPriorityClass(currentLead.priority)}`}>
                {currentLead.priority}
              </span>
              <span className={`crm-status-badge ${getStatusClass(currentLead.status)}`}>
                {currentLead.status}
              </span>
              {currentLead.tags && currentLead.tags.map(tag => (
                <span key={tag} className="crm-tag">
                  {tag}
                </span>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            className="crm-modal-close"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="crm-tabs">
          <button
            className={`crm-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            <FiUser /> Informações
          </button>
          <button
            className={`crm-tab ${activeTab === 'sales' ? 'active' : ''}`}
            onClick={() => setActiveTab('sales')}
          >
            <FiDollarSign /> Vendas & Financeiro
          </button>
          <button
            className={`crm-tab ${activeTab === 'timeline' ? 'active' : ''}`}
            onClick={() => setActiveTab('timeline')}
          >
            <FiActivity /> Histórico
          </button>
        </div>

        <div className="crm-modal-body">
          {/* Info Tab */}
          {activeTab === 'info' && (
            <>
              <div className="crm-info-cards">
                <div className="crm-info-card">
                  <div className="crm-info-card-label">
                    <FiDollarSign />
                    Valor do Negócio
                  </div>
                  <div className="crm-info-card-value">
                    {formatCurrency(currentLead.deal_value)}
                  </div>
                </div>

                <div className="crm-info-card">
                  <div className="crm-info-card-label">
                    <FiTarget />
                    Pontuação
                  </div>
                  <div className="crm-info-card-value crm-info-card-small">
                    {currentLead.lead_score}/100
                  </div>
                  <div className="crm-score-indicator">
                    <div
                      className="crm-score-fill"
                      style={{
                        width: `${currentLead.lead_score}%`,
                        background: getScoreColor(currentLead.lead_score)
                      }}
                    />
                  </div>
                </div>

                {currentLead.expected_close_date && (
                  <div className="crm-info-card">
                    <div className="crm-info-card-label">
                      <FiCalendar />
                      Previsão de Fechamento
                    </div>
                    <div className="crm-info-card-value crm-info-card-small">
                      {new Date(currentLead.expected_close_date).toLocaleDateString('pt-BR')}
                    </div>
                  </div>
                )}

                <div className="crm-info-card">
                  <div className="crm-info-card-label">
                    <FiClock />
                    Criado em
                  </div>
                  <div className="crm-info-card-value crm-info-card-small">
                    {new Date(currentLead.created_at).toLocaleDateString('pt-BR')}
                  </div>
                </div>
              </div>

              <div className="crm-modal-section">
                <h3 className="crm-section-title">
                  <FiUser />
                  Informações de Contato
                </h3>

                <div className="crm-contact-grid">
                  {currentLead.email && (
                    <div className="crm-contact-item">
                      <FiMail color="var(--modal-accent)" />
                      <span>{currentLead.email}</span>
                    </div>
                  )}
                  {currentLead.phone && (
                    <div className="crm-contact-item">
                      <FiPhone color="var(--modal-accent)" />
                      <span>{currentLead.phone}</span>
                    </div>
                  )}
                  {currentLead.company && (
                    <div className="crm-contact-item">
                      <FiBriefcase color="var(--modal-accent)" />
                      <span>{currentLead.company}</span>
                    </div>
                  )}
                </div>
              </div>

              {currentLead.description && (
                <div className="crm-modal-section">
                  <h3 className="crm-section-title">
                    <FiFileText />
                    Descrição
                  </h3>
                  <p className="crm-description-text">
                    {currentLead.description}
                  </p>
                </div>
              )}

              <div className="crm-modal-section">
                <h3 className="crm-section-title">
                  <FiMessageSquare />
                  Adicionar Nota
                </h3>
                <div className="crm-note-input-wrapper">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Escreva uma nota sobre este lead..."
                    className="crm-textarea"
                    disabled={sendingNote}
                  />
                  <button
                    onClick={handleAddNote}
                    className="crm-btn crm-btn-primary crm-self-start"
                    disabled={!newNote.trim() || sendingNote}
                  >
                    {sendingNote ? (
                      <span className="crm-spinner" />
                    ) : (
                      <FiSend />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Sales Tab */}
          {activeTab === 'sales' && (
            <>
              {/* Financial Summary Cards */}
              {financialSummary && (
                <div className="crm-info-cards">
                  <div className="crm-info-card crm-info-card-success">
                    <div className="crm-info-card-label">
                      <FiShoppingCart />
                      Total Vendido
                    </div>
                    <div className="crm-info-card-value">
                      {formatCurrency(financialSummary.total_sales)}
                    </div>
                    <div className="crm-info-card-subtitle">
                      {financialSummary.sales_count} venda(s)
                    </div>
                  </div>

                  <div className="crm-info-card crm-info-card-info">
                    <div className="crm-info-card-label">
                      <FiTrendingUp />
                      Receitas
                    </div>
                    <div className="crm-info-card-value">
                      {formatCurrency(financialSummary.total_revenue)}
                    </div>
                  </div>

                  <div className="crm-info-card crm-info-card-danger">
                    <div className="crm-info-card-label">
                      <FiTrendingDown />
                      Despesas
                    </div>
                    <div className="crm-info-card-value">
                      {formatCurrency(financialSummary.total_expenses)}
                    </div>
                  </div>

                  <div className={`crm-info-card ${financialSummary.profit >= 0 ? 'crm-info-card-success' : 'crm-info-card-danger'}`}>
                    <div className="crm-info-card-label">
                      <FiPercent />
                      Lucro/Prejuízo
                    </div>
                    <div className="crm-info-card-value">
                      {formatCurrency(financialSummary.profit)}
                    </div>
                  </div>
                </div>
              )}

              {/* Sales Section */}
              <div className="crm-modal-section">
                <div className="crm-section-header">
                  <h3 className="crm-section-title">
                    <FiShoppingCart />
                    Vendas
                  </h3>
                  <button
                    className="crm-btn crm-btn-primary crm-btn-sm"
                    onClick={() => {
                      setEditingSale(null);
                      resetSaleForm();
                      setShowSaleForm(true);
                    }}
                  >
                    <FiPlus /> Nova Venda
                  </button>
                </div>

                {showSaleForm && (
                  <div className="crm-form-inline">
                    <form onSubmit={handleSaleSubmit} className="crm-form">
                      <div className="crm-form-row">
                        <div className="crm-form-group">
                          <label>Data da Venda *</label>
                          <input
                            type="date"
                            value={saleForm.sale_date}
                            onChange={(e) => setSaleForm({ ...saleForm, sale_date: e.target.value })}
                            required
                          />
                        </div>
                        <div className="crm-form-group">
                          <label>Valor da Venda *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={saleForm.sale_value}
                            onChange={(e) => setSaleForm({ ...saleForm, sale_value: e.target.value })}
                            required
                          />
                        </div>
                        <div className="crm-form-group">
                          <label>Desconto</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={saleForm.discount}
                            onChange={(e) => setSaleForm({ ...saleForm, discount: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="crm-form-row">
                        <div className="crm-form-group">
                          <label>Forma de Pagamento</label>
                          <select
                            value={saleForm.payment_method}
                            onChange={(e) => setSaleForm({ ...saleForm, payment_method: e.target.value })}
                          >
                            <option value="">Selecione...</option>
                            <option value="PIX">PIX</option>
                            <option value="CARTAO_CREDITO">Cartão de Crédito</option>
                            <option value="CARTAO_DEBITO">Cartão de Débito</option>
                            <option value="BOLETO">Boleto</option>
                            <option value="TRANSFERENCIA">Transferência</option>
                            <option value="DINHEIRO">Dinheiro</option>
                          </select>
                        </div>
                        <div className="crm-form-group">
                          <label>Parcelas</label>
                          <input
                            type="number"
                            min="1"
                            max="24"
                            value={saleForm.installments}
                            onChange={(e) => setSaleForm({ ...saleForm, installments: parseInt(e.target.value) || 1 })}
                          />
                        </div>
                      </div>
                      <div className="crm-form-group">
                        <label>Descrição</label>
                        <textarea
                          placeholder="Detalhes da venda..."
                          value={saleForm.description}
                          onChange={(e) => setSaleForm({ ...saleForm, description: e.target.value })}
                          rows={2}
                        />
                      </div>
                      <div className="crm-form-actions">
                        <button type="submit" className="crm-btn crm-btn-primary" disabled={salesLoading}>
                          {editingSale ? 'Atualizar' : 'Salvar'} Venda
                        </button>
                        <button
                          type="button"
                          className="crm-btn crm-btn-secondary"
                          onClick={() => {
                            setShowSaleForm(false);
                            setEditingSale(null);
                            resetSaleForm();
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {salesLoading ? (
                  <div className="crm-loading-container">
                    <span className="crm-spinner crm-spinner-lg" />
                  </div>
                ) : sales.length === 0 ? (
                  <div className="crm-empty-state">
                    <FiShoppingCart size={32} className="crm-icon-faded" />
                    <p>Nenhuma venda registrada ainda</p>
                    <p className="crm-text-muted">Clique em "Nova Venda" para registrar a primeira venda deste lead</p>
                  </div>
                ) : (
                  <div className="crm-sales-list">
                    {sales.map((sale) => (
                      <div key={sale.id} className="crm-sale-item">
                        <div className="crm-sale-info">
                          <div className="crm-sale-main">
                            <span className="crm-sale-value">{formatCurrency(sale.final_value)}</span>
                            {sale.discount > 0 && (
                              <span className="crm-sale-discount">
                                (Desc: {formatCurrency(sale.discount)})
                              </span>
                            )}
                            <span className={`crm-sale-status crm-sale-status-${sale.status.toLowerCase()}`}>
                              {sale.status === 'CONFIRMED' ? 'Confirmada' :
                               sale.status === 'PENDING' ? 'Pendente' :
                               sale.status === 'CANCELLED' ? 'Cancelada' : 'Reembolsada'}
                            </span>
                          </div>
                          <div className="crm-sale-details">
                            <span><FiCalendar /> {new Date(sale.sale_date).toLocaleDateString('pt-BR')}</span>
                            {sale.payment_method && (
                              <span><FiCreditCard /> {sale.payment_method.replace('_', ' ')}</span>
                            )}
                            {sale.installments > 1 && (
                              <span>{sale.installments}x</span>
                            )}
                          </div>
                          {sale.description && (
                            <p className="crm-sale-description">{sale.description}</p>
                          )}
                        </div>
                        <div className="crm-sale-actions">
                          <button
                            className="crm-btn-icon"
                            onClick={() => handleEditSale(sale)}
                            title="Editar"
                          >
                            <FiEdit />
                          </button>
                          <button
                            className="crm-btn-icon crm-btn-icon-danger"
                            onClick={() => handleDeleteSale(sale.id)}
                            title="Excluir"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Transactions Section */}
              <div className="crm-modal-section">
                <div className="crm-section-header">
                  <h3 className="crm-section-title">
                    <FiActivity />
                    Receitas & Despesas
                  </h3>
                  <button
                    className="crm-btn crm-btn-primary crm-btn-sm"
                    onClick={() => {
                      setEditingTransaction(null);
                      resetTransactionForm();
                      setShowTransactionForm(true);
                    }}
                  >
                    <FiPlus /> Nova Transação
                  </button>
                </div>

                {showTransactionForm && (
                  <div className="crm-form-inline">
                    <form onSubmit={handleTransactionSubmit} className="crm-form">
                      <div className="crm-form-row">
                        <div className="crm-form-group">
                          <label>Tipo *</label>
                          <select
                            value={transactionForm.type}
                            onChange={(e) => setTransactionForm({ ...transactionForm, type: e.target.value })}
                            required
                          >
                            <option value="REVENUE">Receita</option>
                            <option value="EXPENSE">Despesa</option>
                          </select>
                        </div>
                        <div className="crm-form-group">
                          <label>Valor *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={transactionForm.amount}
                            onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                            required
                          />
                        </div>
                        <div className="crm-form-group">
                          <label>Data *</label>
                          <input
                            type="date"
                            value={transactionForm.transaction_date}
                            onChange={(e) => setTransactionForm({ ...transactionForm, transaction_date: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <div className="crm-form-row">
                        <div className="crm-form-group">
                          <label>Categoria</label>
                          <input
                            type="text"
                            placeholder="Ex: Comissão, Marketing, Frete..."
                            value={transactionForm.category}
                            onChange={(e) => setTransactionForm({ ...transactionForm, category: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="crm-form-group">
                        <label>Descrição</label>
                        <textarea
                          placeholder="Detalhes da transação..."
                          value={transactionForm.description}
                          onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                          rows={2}
                        />
                      </div>
                      <div className="crm-form-actions">
                        <button type="submit" className="crm-btn crm-btn-primary" disabled={salesLoading}>
                          {editingTransaction ? 'Atualizar' : 'Salvar'} Transação
                        </button>
                        <button
                          type="button"
                          className="crm-btn crm-btn-secondary"
                          onClick={() => {
                            setShowTransactionForm(false);
                            setEditingTransaction(null);
                            resetTransactionForm();
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {transactions.length === 0 ? (
                  <div className="crm-empty-state">
                    <FiActivity size={32} className="crm-icon-faded" />
                    <p>Nenhuma transação registrada ainda</p>
                  </div>
                ) : (
                  <div className="crm-transactions-list">
                    {transactions.map((transaction) => (
                      <div
                        key={transaction.id}
                        className={`crm-transaction-item crm-transaction-${transaction.type.toLowerCase()}`}
                      >
                        <div className="crm-transaction-icon">
                          {transaction.type === 'REVENUE' ? (
                            <FiTrendingUp color="#22c55e" />
                          ) : (
                            <FiTrendingDown color="#ef4444" />
                          )}
                        </div>
                        <div className="crm-transaction-info">
                          <div className="crm-transaction-main">
                            <span className={`crm-transaction-amount ${transaction.type.toLowerCase()}`}>
                              {transaction.type === 'EXPENSE' ? '- ' : '+ '}
                              {formatCurrency(transaction.amount)}
                            </span>
                            {transaction.category && (
                              <span className="crm-transaction-category">{transaction.category}</span>
                            )}
                          </div>
                          <div className="crm-transaction-details">
                            <span><FiCalendar /> {new Date(transaction.transaction_date).toLocaleDateString('pt-BR')}</span>
                          </div>
                          {transaction.description && (
                            <p className="crm-transaction-description">{transaction.description}</p>
                          )}
                        </div>
                        <div className="crm-transaction-actions">
                          <button
                            className="crm-btn-icon"
                            onClick={() => handleEditTransaction(transaction)}
                            title="Editar"
                          >
                            <FiEdit />
                          </button>
                          <button
                            className="crm-btn-icon crm-btn-icon-danger"
                            onClick={() => handleDeleteTransaction(transaction.id)}
                            title="Excluir"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div>
              <h3 className="crm-section-title">
                <FiActivity />
                Histórico de Atividades
              </h3>

              {loading ? (
                <div className="crm-loading-container">
                  <span className="crm-spinner crm-spinner-lg" />
                  <span className="crm-ml-3">Carregando...</span>
                </div>
              ) : timeline.length === 0 ? (
                <div className="crm-empty-state">
                  <FiAlertCircle size={32} className="crm-icon-faded" />
                  <p>Nenhuma atividade registrada ainda</p>
                </div>
              ) : (
                <div className="crm-timeline">
                  {timeline.map((activity, index) => (
                    <div key={activity.id || index} className="crm-timeline-item">
                      <div className="crm-timeline-icon">
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="crm-timeline-content">
                        <div className="crm-timeline-title">
                          {activity.description}
                        </div>
                        <div className="crm-timeline-time">
                          {formatDate(activity.created_at)}
                        </div>
                        {activity.activity_metadata && Object.keys(activity.activity_metadata).length > 0 && (
                          <div className="crm-timeline-description">
                            {JSON.stringify(activity.activity_metadata, null, 2)}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="crm-modal-footer">
          <button
            onClick={onClose}
            className="crm-btn crm-btn-secondary"
          >
            Fechar
          </button>
          <button
            className="crm-btn crm-btn-primary"
            onClick={() => setShowEditForm(true)}
          >
            <FiEdit />
            Editar Lead
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailModal;
